import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Typography from "@mui/material/Typography"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

const skipTransient = { shouldForwardProp: (p: string) => !p.startsWith("$") }

/* EGDE-498: thin, unobtrusive scrollbar for the desktop inner-scroll region. */
const thinScrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: ${colors.primitives.neutral300} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.primitives.neutral300};
    border-radius: 3px;
  }
`

export const Backdrop = styled("div", skipTransient)<{
  $isOpen: boolean
}>`
  position: fixed;
  inset: 0;
  z-index: 1003;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(p) => (p.$isOpen ? 1 : 0)};
  pointer-events: ${(p) => (p.$isOpen ? "auto" : "none")};
  transition: opacity 0.3s ease;
`

export const DrawerPanel = styled("div", skipTransient)<{
  $isOpen: boolean
}>`
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 1004;
  width: 100%;
  background: ${colors.primitives.neutral0};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.3s ease-in-out, visibility 0.3s;
  visibility: ${(p) => (p.$isOpen ? "visible" : "hidden")};

  left: 0;
  transform: ${(p) => (p.$isOpen ? "translateX(0)" : "translateX(-100%)")};

  @media (min-width: ${breakpoints.lg}px) {
    width: 440px;
    top: ${sizes.spacing[12]}px;
    bottom: ${sizes.spacing[12]}px;
    /* EGDE-302: the floating drawer must sit 120px in from the open (right, in
       RTL) edge — Figma spec. Was spacing[12] (12px) — i.e. "aligned to the
       right too much". stylis-plugin-rtl flips left into right for Hebrew. */
    left: ${sizes.spacing[120]}px;
    border-radius: ${sizes.borderRadius[24]}px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  }
`

export const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* Setting only overflow-y makes overflow-x compute to auto; the expanded
     category panel bleeds a few px past its container, which then shows a
     horizontal scrollbar on Windows. Clip the cross axis explicitly. */
  overflow-x: hidden;

  /* EGDE-498: on desktop the drawer is a fixed-height floating panel, so the
     whole thing must not scroll — only the main links (MenuItems) scroll
     internally. Header stays pinned on top; the "other sites" section and the
     bottom strip stay pinned below. (EGDE-669 removed the language pill that
     used to sit between them.) */
  @media (min-width: ${breakpoints.lg}px) {
    overflow: hidden;
  }
`

/* ESEIM-328: Top padding to match Figma design spacing above header bar */
/* ESEIM-329: Align header items properly with consistent padding */
/* EGDE-307: top padding is 32px per Figma (was 48px — too much). */
export const DrawerHeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  min-height: 56px;
  padding: ${sizes.spacing[32]}px ${sizes.spacing[16]}px ${sizes.spacing[16]}px;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[32]}px ${sizes.spacing[32]}px ${sizes.spacing[24]}px;
  }
`

export const HeaderIconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[12]}px;
`

export const HeaderIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${colors.primitives.neutral600};
  transition: opacity 0.2s;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const HeaderLogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
  /* EGDE-320: keep the logo in normal flex flow (matches Figma — the header is a
     justify-between row of CTA · logo · icons). Absolute centering was pulling the
     logo out of flow, so a long contact-CTA label (e.g. "צרו איתנו קשר") slid over
     it. align-items:center on DrawerHeaderBar handles the vertical alignment. */
`

export const HeaderLogoImage = styled.img`
  display: block;
  width: 120px;
  height: 42px;
  object-fit: contain;
`

/* EGDE-320: shared so the contact CTA can render as a Link (when contactCta
   resolves to a URL) or as a <button> (when it opens the global contact modal). */
const headerCtaStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 ${sizes.spacing[16]}px;
  border-radius: ${sizes.borderRadius.full}px;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s;
  position: relative;
  color: ${colors.primitives.neutral600};
  background: ${colors.primitives.neutral0};
  font-family: inherit;
  border: none;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: ${colors.gradients.gradientCta};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const HeaderCtaButton = styled(Link)`
  ${headerCtaStyles}
`

export const HeaderCtaButtonAsButton = styled.button`
  ${headerCtaStyles}
`

/* ESEIM-336: Categories section with spacing between items */
/* EGDE-498: the Figma side-menu component stacks category rows directly —
   each row is 54px tall (38px text box + 8px vertical padding), with no extra
   flex gap between rows. */
/* EGDE-498: the middle area between the fixed header and fixed footer. On
   mobile it is transparent (display: contents) so the whole panel scrolls as
   before. On desktop it is the scroll region: main links + any open submenu +
   "other sites" scroll together, and only the header above and the bottom strip
   below the divider stay pinned.

   EGDE-653: that used to be true only while a submenu was open — closed, the
   scroll was handed down to MenuItems alone, so the visitor could only move
   the link list inside a ~150px window while "other sites" sat frozen. QA:
   "all the part up to the divider line should be scrollable… only a small part
   is scrollable." One scroll region in both states, so the drawer behaves the
   same however the visitor got there. */
export const MiddleArea = styled.div`
  display: contents;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    /* Clip the cross axis so the expanded panel's negative-margin bleed
       doesn't surface a horizontal scrollbar (Windows). */
    overflow-x: hidden;
    ${thinScrollbar}
  }
`

export const MenuItems = styled.nav`
  display: flex;
  flex-direction: column;
  padding: ${sizes.spacing[16]}px ${sizes.spacing[32]}px 0;

  /* EGDE-572: with no categories the desktop \`flex: 1\` below still stretched
     this nav, reserving ~300px of blank drawer above "other sites" — the
     drawer looked broken rather than merely link-less. An empty nav now
     collapses, so a menu outage degrades to a shorter drawer. */
  &:empty {
    display: none;
  }

  /* EGDE-653: the parent MiddleArea now owns the desktop scroll in every state
     (see there), so the nav never scrolls inside itself. It still GROWS into
     any leftover space — flex-grow with a content flex-basis and no shrink —
     which is what keeps "other sites" sitting on the divider when the link list
     is short enough to fit. Once the list overflows
     there is no free space to grow into, the nav settles at its natural height
     and MiddleArea scrolls the lot. */
  @media (min-width: ${breakpoints.lg}px) {
    flex: 1 0 auto;
    overflow-y: visible;
  }
`

/* ESEIM-333: Remove divider lines between categories */
export const MenuItem = styled.div`
  /* No border between categories per Figma design */
`

/* EGDE-498: hovering a category reveals the gradient chevron at the title's
   inline-start (Figma hover state) — the text itself stays dark. The chevron
   glyph natively points LEFT; mirror it for LTR exactly like the hero's
   ChevronAccent (stylis does not flip transforms, so scope by [dir]). */
export const HoverChevron = styled.span`
  display: none;
  align-items: center;
  flex-shrink: 0;
  margin-inline-end: ${sizes.spacing[8]}px;

  svg {
    height: 20px;
    width: auto;
    transform: scaleX(-1);
  }

  [dir="rtl"] & svg {
    transform: none;
  }
`

/* EGDE-498: chevron + title travel together at the row's inline-start while
   the +/- toggle keeps to the opposite edge via justify-content. */
export const MenuTitleGroup = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`

/* ESEIM-326 / EGDE-498: category hover shows the chevron; text stays dark */
export const MenuLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* EGDE-498: 54px row per the Figma component (38px text box + 8px padding). */
  min-height: 54px;
  padding: ${sizes.spacing[8]}px 0;
  text-decoration: none;
  color: ${(p) => (p.$active ? colors.primitives.primary500 : colors.primitives.neutral600)};

  &:hover ${HoverChevron} {
    display: flex;
  }
`

/* ESEIM-326 / EGDE-498: category hover shows the chevron; text stays dark */
export const MenuButton = styled("button", skipTransient)<{
  $expanded?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* EGDE-498: 54px row per the Figma component (38px text box + 8px padding). */
  min-height: 54px;
  padding: ${sizes.spacing[8]}px 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.primitives.neutral600};

  &:hover ${HoverChevron} {
    display: flex;
  }
`

/* EGDE-498: category titles are Heading-3 in the Figma side-menu component —
   24px/28 bold (were 32px per the since-superseded ESEIM-334). */
export const MenuTitle = styled(Typography)`
  color: inherit;
  font-weight: 700 !important;
  font-size: 24px !important;
  line-height: 28px !important;
`

export const ExpandIconWrapper = styled("div", skipTransient)<{
  $expanded: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primitives.primary500};
`

/* ESEIM-338 / EGDE-498: expanded category gets the soft orange panel only —
   the Figma panel carries NO side accent bar (the old 4px gradient ::before
   also ended up on the wrong side once stylis flipped right:0 for RTL). The
   panel bleeds flush to the drawer's inline-start edge (32px of the MenuItems
   padding) and 8px toward the inline-end, so only the inline-end corners are
   rounded. */
export const MenuItemWrapper = styled("div", skipTransient)<{
  $expanded?: boolean
}>`
  position: relative;
  border-start-start-radius: 0;
  border-end-start-radius: 0;
  border-start-end-radius: ${sizes.borderRadius[8]}px;
  border-end-end-radius: ${sizes.borderRadius[8]}px;
  /* No background transition: the box geometry (padding/margin) changes
     instantly, so fading the fill would leave the colored box in the wrong
     (collapsed) geometry on close — a visible "cut". Snap the fill in sync. */
  /* #FFF7F0 sampled from the Figma expanded-panel fill */
  background-color: ${(p) => (p.$expanded ? "#FFF7F0" : "transparent")};
  /* The rows carry their own 8px vertical padding, so the panel only needs the
     horizontal bleed. */
  padding-inline-start: ${(p) => (p.$expanded ? `${sizes.spacing[32]}px` : "0")};
  padding-inline-end: ${(p) => (p.$expanded ? "38px" : "0")};
  margin-inline-start: ${(p) =>
    p.$expanded ? `-${sizes.spacing[32]}px` : "0"};
  margin-inline-end: ${(p) => (p.$expanded ? "-35px" : "0")};
`

export const SubMenu = styled("ul", skipTransient)<{
  $expanded: boolean
}>`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[4]}px;
  overflow: hidden;
  max-height: ${(p) => (p.$expanded ? "500px" : "0")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  padding-bottom: ${(p) => (p.$expanded ? `${sizes.spacing[12]}px` : "0")};
  padding-top: ${(p) => (p.$expanded ? `${sizes.spacing[4]}px` : "0")};
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease,
    padding-bottom 0.3s ease,
    padding-top 0.3s ease;
  list-style: none;
  margin: 0;
  padding-left: 0;
  padding-right: 0;
`

/* ESEIM-327: Visual highlight for active sub-item */
/* EGDE-498: sub-items are Body-2 in the Figma component — 16px/20 at every
   width (the 18px desktop bump had no design source). */
export const SubLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  text-decoration: none;
  color: ${(p) => (p.$active ? colors.primitives.primary500 : colors.primitives.neutral600)};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  padding: ${sizes.spacing[8]}px 0;
  font-size: 16px;
  line-height: 20px;
  display: block;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primitives.primary500};
  }
`

/* ESEIM-336: Spacing between categories and quick links section */
/* EGDE-498: 24px section gap (plus the last row's 8px padding) per the Figma
   component — was 40px. */
export const QuickLinksSection = styled.div`
  padding: ${sizes.spacing[24]}px ${sizes.spacing[32]}px 0;
  flex-shrink: 0;
`

/* EGDE-498: "אתרים נוספים" label is Body-2 Regular — 16px/20 per the Figma
   component (was 18px per the since-superseded ESEIM-332). */
export const QuickLinksLabel = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-size: 16px !important;
  line-height: 20px !important;
  font-weight: 500 !important;
  margin-bottom: ${sizes.spacing[16]}px;
`

/* ESEIM-337: Arrow direction handled via scaleX in component */
/* EGDE-498: quick links are Body-1 Bold — 18px/22, 6px gap to a 16px arrow per
   the Figma component (were 20px/8px per the since-superseded ESEIM-335). */
export const QuickLinkItem = styled.a`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[6]}px;
  padding: ${sizes.spacing[8]}px 0;
  text-decoration: none;
  color: ${colors.primitives.neutral600};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;

  /* EGDE-498: per Figma the arrow is dark at rest and turns orange on
     hover/focus; the label stays dark in both states. The arrow also nudges
     3px toward the inline-start (right in RTL) on hover/focus. The LTR flip
     lives here, scoped by [dir], because stylis-plugin-rtl does not flip
     transforms — so it can compose with the hover/focus translate. */
  svg {
    color: ${colors.primitives.neutral600};
    flex-shrink: 0;
    transition:
      color 0.2s ease,
      transform 0.2s ease;
  }

  [dir="ltr"] & svg {
    transform: scaleX(-1);
  }

  &:hover svg,
  &:focus-visible svg {
    color: ${colors.primitives.primary500};
    transform: translateX(3px);
  }

  /* In LTR the arrow is mirrored, so the +3px local shift resolves to a -3px
     visual move — same direction as RTL. */
  [dir="ltr"] &:hover svg,
  [dir="ltr"] &:focus-visible svg {
    transform: scaleX(-1) translateX(3px);
  }
`

/* ESEIM-336: Bottom strip with correct spacing between CTA, social icons, and email */
/* EGDE-301: divider line separating the quick-links section from the bottom
   CTA/social/email strip (matches the Footer divider convention). */
/* EGDE-345: divider above the bottom strip was neutral200 (#f1f1f1) — too faint
   to read. Use neutral300 (#d0d0d0) so the line is clearly visible per Figma. */
export const BottomStrip = styled.div`
  margin-top: auto;
  flex-shrink: 0;
  padding: ${sizes.spacing[32]}px ${sizes.spacing[32]}px ${sizes.spacing[32]}px;
  border-top: 1px solid ${colors.primitives.neutral300};
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
`

/* ESEIM-331: CTA button text needs black color */
/* EGDE-498: 20px/28 bold on a 12px-padded pill (52px tall) per the Figma
   component — was 48px tall with 16px text. */
export const CtaButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  color: ${colors.primitives.neutral600};
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

export const SocialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
`

/* Social links are external URLs — use styled.a, not styled(Link) */
export const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-decoration: none;
  color: ${colors.primitives.neutral600};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  /* EGDE-498: social icons are 26×26 in the current Figma side-menu component
     (EGDE-344's 20×20 came from an older frame). */
  img {
    width: 26px;
    height: 26px;
  }

  svg {
    width: 26px;
    height: 26px;
  }
`

/* ESEIM-330: Email centered + underline */
/* EGDE-498: 16px/20 per the Figma component (was 14px). */
export const EmailLink = styled.a`
  text-decoration: underline;
  text-align: center;
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  line-height: 20px;
  display: block;

  &:hover {
    color: ${colors.primitives.primary500};
  }
`
